// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useFilteredVariantTraits from "@insite/client-framework/Common/Hooks/useFilteredVariantTraits";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getIsProductCompareFull,
    getProductCompareChecked,
} from "@insite/client-framework/Store/Components/CompareProductsDrawer/CompareProductsDrawerSelectors";
import addProductToCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/AddProductToCompare";
import removeProductFromCompare from "@insite/client-framework/Store/Components/CompareProductsDrawer/Handlers/RemoveProductFromCompare";
import { getProductSelector } from "@insite/client-framework/Store/Components/ProductSelector/ProductSelectorSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    getProductState,
    getVariantChildrenDataView,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import Select, { SelectProps } from "@insite/mobius/Select";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@rc/App/Store/Reducers";
import { xProductListState } from "@rc/App/Store/Reducers";
import { carouselTypeOptions } from "@rc/Overrides/Widgets/Common/ProductCarousel";
import { XngHasProductContext } from "@rc/Overrides/Widgets/ProductList/ProductListProductContext";
import React, { FC, useContext, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showPrice: boolean;
    showAddToList: boolean;

    // xng-change
    showCompare?: boolean;
    showAddToCart?: boolean;
}

const mapStateToProps = (state: CustomAppState, props: XngHasProductContext) => {
    const { variantSelection } = getProductSelector(state);

    return {
        productSettings: getSettingsCollection(state).productSettings,

        // xng-change
        view: state.pages.productList.view || getSettingsCollection(state).productSettings.defaultViewType,
        settingsCollection: getSettingsCollection(state),
        productCompareChecked: getProductCompareChecked(state, props.productContext),
        isProductCompareFull: getIsProductCompareFull(state),
        variantParentProduct: getProductState(
            state,
            props.productContext?.parentProduct
                ? props.productContext?.parentProduct.id
                : props.productContext.product.id,
        ).value,
        variantChildren: props.productContext?.parentProduct
            ? getVariantChildrenDataView(state, props.productContext?.parentProduct.id).value
            : getVariantChildrenDataView(state, props.productContext.product.id).value,
        variantSelection,
        plpStoreParentProducts: state.xProductList.storeParentProducts,
        recentlyViewedStoreParentProducts: state.xRecentlyViewedCarousel.recentlyViewedStoreParentProducts,
    };
};

const mapDispatchToProps = {
    addProductToCompare,
    removeProductFromCompare,
};

type Props = ReturnType<typeof mapStateToProps> &
    XngHasProductContext &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps;

export interface ProductListActionsStyles {
    wrapper?: InjectableCss;
    addToCartWrapper?: InjectableCss;
    addToCartButton?: ButtonPresentationProps;
    quantityOrdered?: TextFieldProps;
    unitOfMeasureSelect?: SelectPresentationProps;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    price?: ProductPriceStyles;
    addToListLink?: ProductAddToListLinkStyles;
    addToListWrapper?: InjectableCss;

    // xng-change
    actions?: InjectableCss;
    compareCheckbox?: CheckboxPresentationProps;
    variantSelect?: InjectableCss;
}

export const actionsStyles: ProductListActionsStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;

            &.marginTop {
                margin-top: auto;
            }

            &.minHeight {
                min-height: 62px;
            }
        `,
    },
    unitOfMeasureSelect: {
        cssOverrides: {
            formField: css`
                margin-top: 20px;
            `,
        },
    },
    quantityBreakPricing: {
        viewLink: {
            css: css`
                margin: 8px 0;
            `,
        },
    },
    addToCartWrapper: {
        css: css`
            display: flex;
            align-items: center;
            margin-top: 15px;
        `,
    },
    addToCartButton: {
        css: css`
            width: 80%;
            margin-left: 27px;
            padding: 0;
        `,
    },
    quantityOrdered: {},
    addToListWrapper: {
        css: css`
            margin-top: 20px;
            width: 50%;
            text-align: right;
        `,
    },
    actions: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: flex-end;
            align-items: center;
            min-height: 45px;
        `,
    },
    compareCheckbox: {
        css: css`
            align-self: center;
            margin-left: 0;
        `,
    },
    variantSelect: {
        css: css`
            margin-top: 10px;
        `,
    },
};

const styles = actionsStyles;

const ProductListActions: FC<Props> = ({
    productContext,
    productSettings,
    showPrice,
    showAddToList,
    showAddToCart,
    showCompare,
    view,
    settingsCollection,
    isProductCompareFull,
    productCompareChecked,
    addProductToCompare,
    removeProductFromCompare,

    //  xng-change
    variantParentProduct,
    variantChildren,
    variantSelection,
    plpStoreParentProducts,
    recentlyViewedStoreParentProducts,
}) => {
    const toasterContext = useContext(ToasterContext);
    const dispatch = useDispatch();
    const carouselType = productContext?.carouselType;

    const [selectedTraitValue, setSelectedTraitValue] = useState(() => {
        if (productContext?.product?.isVariantParent) {
            return "";
        }
        return productContext?.product?.childTraitValues?.[0]?.id;
    });

    const handleChanged = (_: React.SyntheticEvent, value: boolean) => {
        if (value && isProductCompareFull) {
            toasterContext.addToast({
                body: siteMessage("ProductionCompare_MaximumItemsReached"),
                messageType: "warning",
            });
        } else if (value) {
            addProductToCompare({ productId: productContext.product.id });
        } else {
            removeProductFromCompare({ productId: productContext.product.id });
        }
    };

    const getSelectedChild = (traitValueId: string) => {
        const storeParentProduct = carouselType?.length
            ? recentlyViewedStoreParentProducts.filter((item: ProductModel) => item.id === variantParentProduct?.id)[0]
            : plpStoreParentProducts.filter((item: ProductModel) => item.id === variantParentProduct?.id)[0];

        if (storeParentProduct) {
            const variantChildren = (storeParentProduct as any).variantChildren;

            for (const variantChild of variantChildren) {
                let matches = true;
                if (!variantChild.childTraitValues) {
                    break;
                }
                for (const childTraitValue of variantChild.childTraitValues) {
                    if (traitValueId !== childTraitValue.id) {
                        matches = false;
                        break;
                    }
                }

                if (matches) {
                    return variantChild;
                }
            }
        }
    };

    const updateStoreParentProducts = (parentProductId: string | undefined, selectedChild: ProductModel) => {
        if (!carouselType) {
            const newProducts = plpStoreParentProducts.map((item: ProductModel) => {
                if (item.id === parentProductId) {
                    return {
                        ...item,
                        selectedChild,
                    };
                }
                return item;
            });

            if (newProducts?.length) {
                dispatch({
                    type: "xProductList/prepareParentProducts",
                    storeParentProducts: newProducts,
                });
            }
        } else {
            const newProducts = recentlyViewedStoreParentProducts.map((item: ProductModel) => {
                if (item.id === parentProductId) {
                    return {
                        ...item,
                        selectedChild,
                    };
                }
                return item;
            });

            if (newProducts?.length) {
                dispatch({
                    type: "xRecentlyViewedCarousel/prepareParentProducts",
                    recentlyViewedStoreParentProducts: newProducts,
                });
            }
        }
    };

    const variantChangeHandler = (traitValueId: string, variantTraitId: string) => {
        setSelectedTraitValue(traitValueId);
        const parentProductId = variantParentProduct?.id;
        const selectedChild = getSelectedChild(traitValueId);
        updateStoreParentProducts(parentProductId, selectedChild);
    };

    let filteredVariantTraits: any[] = [];
    if (productContext.product.isVariantParent) {
        filteredVariantTraits = useFilteredVariantTraits(
            variantParentProduct?.variantTraits as any,
            variantChildren || [],
            variantSelection,
        );
    } else if (productContext?.parentProduct?.isVariantParent) {
        filteredVariantTraits = useFilteredVariantTraits(
            productContext?.parentProduct?.variantTraits as any,
            variantChildren || [],
            variantSelection,
        );
    }

    return (
        <StyledWrapper
            {...styles.wrapper} className="marginTop"
        >
            {showPrice && (
                <StyledWrapper>
                    <ProductPrice
                        product={productContext}
                        showLabel={false}
                        showSavings={true}
                        showSavingsAmount={productSettings.showSavingsAmount}
                        showSavingsPercent={productSettings.showSavingsPercent}
                        extendedStyles={styles.price}
                    />
                    <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                </StyledWrapper>
            )}

            <StyledWrapper
                {...styles.wrapper}
                className={`${view === "Grid" ||
                        (carouselTypeOptions.some(item => item.value === carouselType) &&
                            recentlyViewedStoreParentProducts?.length > 0)
                        ? "minHeight"
                        : ""
                    }`}
            >
                {filteredVariantTraits.map(variantTrait => (
                    <Select
                        {...styles.variantSelect}
                        key={variantTrait.id.toString()}
                        value={variantSelection[variantTrait.id]}
                        onChange={event => variantChangeHandler(event.currentTarget.value, variantTrait.id)}
                        data-test-selector={`productSelectorVariantModal_Dropdown_${variantTrait.nameDisplay}`}
                    >
                        <option value="">
                            {variantTrait.unselectedValue
                                ? variantTrait.unselectedValue
                                : `${translate("Select")} ${variantTrait.nameDisplay}`}
                        </option>
                        {variantTrait.traitValues?.map((traitValue: any) => (
                            <option
                                selected={traitValue.id === selectedTraitValue}
                                value={`${traitValue.id}`}
                                key={`${traitValue.id}`}
                            >
                                {traitValue.valueDisplay}
                            </option>
                        ))}
                    </Select>
                ))}
            </StyledWrapper>

            <ProductUnitOfMeasureSelect extendedStyles={styles.unitOfMeasureSelect} />
            <StyledWrapper {...styles.addToCartWrapper}>
                {showAddToCart !== false && (
                    <>
                        <ProductQuantityOrdered extendedStyles={styles.quantityOrdered} />
                        <ProductAddToCartButton
                            data-test-selector={`actionsAddToCart${productContext.product.id}`}
                            extendedStyles={styles.addToCartButton}
                        />
                    </>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.actions}>
                {showCompare && settingsCollection.productSettings.enableProductComparisons && (
                    <StyledWrapper
                        css={css`
                            width: 50%;
                            padding-top: 20px;
                            display: flex;
                        `}
                    >
                        <Checkbox
                            {...styles.compareCheckbox}
                            checked={productCompareChecked}
                            onChange={handleChanged}
                            data-test-selector="productList_CompareCheckbox"
                        >
                            {translate("Compare")}
                        </Checkbox>
                    </StyledWrapper>
                )}
                {showAddToList && (
                    <StyledWrapper {...styles.addToListWrapper}>
                        <ProductAddToListLink
                            data-test-selector={`actionsAddToList${productContext.product.id}`}
                            extendedStyles={styles.addToListLink}
                        />
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductListActions));
